import React, { useState, useEffect, useContext } from 'react'
import { useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { Link, useHistory } from 'react-router-dom'
import omit from 'lodash/omit'
import { App, Badge, Breadcrumb, Menu, Row, Col, Modal, Tabs, Alert, TabsProps, Tooltip, Tag } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { RcFile } from 'antd/lib/upload/interface'
import lowerCase from 'lodash/lowerCase'
import sortBy from 'lodash/sortBy'
import dayjs from 'dayjs'
import { useManualQuery } from 'graphql-hooks'
import { useFeatureFlagEnabled } from 'posthog-js/react'

import Api from '@vacationtracker/shared/services/api'
import { getUserById, getConnectedCalendars, getTeamsForApprover, getAutomations, getSuggestedSubstituteApprovers, getInvitations } from '../../../graphql/custom-queries'
import * as logger from '../../../services/logger'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { roleAdmin, roleApprover, selectAuthUserSlice } from '../../../store/auth-user-slice'
import { useShouldEnableFeatures } from '../../../store/use-should-enable-features'
import { selectLeaveRequestActionEventSlice, setLeaveRequestActionEvent } from '../../../store/leave-request-action-event-slice'
import { selectLocaleSlice } from '../../../store/locale-slice'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { notificationStore } from '../../../context/notificationsContext/store'
import { uploadAvatar } from '../../../services/api/files'
import { useSubstituteApprover } from '../../../util/hooks/use-substitute-approver'
import { useEnergyScore } from '../../../hooks/use-energy-score'
import IntlMessages from '../../../util/IntlMessages'
import CircularProgress from '../../../components/circular-progress'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'
import LeaveForm from '@vacationtracker/shared/components/leave-form'
import UserTodayOff from '../../../components/user-today-off'
import UserProfileTab from '../../../components/user-profile-tab'
import UserLeavesTab from '../../../components/user-leaves-tab'
import UserLogsTab from '../../../components/user-logs-tab'
import UserLabels from '../../../components/user-labels'
import NotFoundPage from '../../NotFoundPage'
import UserMyProfileModal from '../../../components/modal-update-my-profile'
import { isToilLeave } from '@vacationtracker/shared/functions/is-toil-leave-request'

import { IQuotaUpdates, IRolloverQuotaUpdates } from '../../../components/user-profile-tab/types'
import {
  IGetAutomations,
  IGetConnectedCalendarsResponse,
  IGetInvitations,
  IGetUser,
  IGetUserById,
  IGetUsersForAdminAddLeaveData,
  IUserHistory,
  IUserPendingLeaves,
  IUserProbationPeriod,
  IUserToday,
  IUserUpcomingLeaves
} from '../../../types/custom-queries'
import { IGetTeamsShort } from '../../../types/teams'
import { IGetLocationsShort } from '../../../types/locations'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { EmailInviteStatusEnum, HourFormatEnum, IUserUpdatedEvent } from '@vacationtracker/shared/types/user'
import { Platform } from '@vacationtracker/shared/types/core-event'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { ShortestLeaveIntervalEnum } from '@vacationtracker/shared/types/leave-policy'
import { ILeaveFormSaveData } from '@vacationtracker/shared/components/leave-form/types'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { IConnectedGoogleCalendar, IConnectedOutlookCalendar } from '@vacationtracker/shared/types/calendar'
import { IResendLeaveRequest } from '@vacationtracker/shared/types/leave-request'
import { FrontendUrls } from '../../../types/urls'
import { GetTeamsForApproverData } from '@vacationtracker/shared/components/add-request-leave-additional-info/types'
import { IWorkingSchedule, WorkingHours } from '@vacationtracker/shared/types/work-week'
import { getAverageWorkingHours, getUserWorkingHoursDependingOnType, userHasRotatingSchedule } from '@vacationtracker/shared/functions/work-week'
import { ApproversLevelEnum } from '@vacationtracker/shared/types/team'
import { ADPFlag } from '../../../components/adp/integrated-flag'
import { ProfileTabEnum, isValidProfileTab } from '../../../types/profile-tabs'

interface ILabel {
  id: string
  name: string
  color: string
  userCount?: number
}

interface IUserPage {
  match: {
    params: {
      id: string
    }
  }
  location: {
    pathname: string
    search: string
  }
}

const UserPage = ({ match, location }: IUserPage): React.ReactElement => {
  const { formatMessage } = useIntl()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const { notification } = App.useApp()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { locale } = useAppSelector(selectLocaleSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)

  const { leaveRequestActionEvent } = useAppSelector(selectLeaveRequestActionEventSlice)
  const dispatch = useAppDispatch()
  const amIAdmin = useSelector(roleAdmin)
  const amIApprover = useSelector(roleApprover)
  const history = useHistory()
  const shouldEnableSubstituteApprovers = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.substituteApprovers)
  const enableMinutePickerLeave = useFeatureFlagEnabled(FeatureFlagEnum.enableMinutePickerLeave)

  // Energy score hook - clean abstraction without Redux complexity
  const { energyScore, shouldShowEnergyScore, isFeatureEnabled: isEnergyScoreEnabled } = useEnergyScore({
    userId: match.params.id,
    currentUserId: authUser?.id,
    companyId: authCompany?.id,
  })

  const [visibleUserUpdateForm, setVisibleUserUpdateForm] = useState(false)
  const [isUserEndDateEnabled, setIsUserEndDateEnabled ] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<IGetUser>({
    id: '',
    name: '',
    imageUrl: '',
    role: '',
    isAdmin: false,
    email: '',
    platform: '',
    status: '',
    startDate: '',
    endDate: '',
    employeeId: '',
    isNameLocked: false,
    leaveDays: [
      {
        yearStart: new Date().toISOString(),
        yearEnd: new Date().toISOString(),
        leaveTypes: [
          {
            leaveTypeId: '',
            leaveTypeName: '',
            leavePolicyId: '',
            defaultDaysPerYear: 0,
            hasUnlimitedDays: false,
            rolloverFromPreviousYear: 0,
            maxRolloverDays: 0,
            expiredDays: 0,
            earnedDays: 0,
            earnedDaysWithoutRollover: 0,
            usedDays: 0,
            scheduledDays: 0,
            usedDaysInPeriod: 0,
            currentDays: 0,
            accrualType: 'NONE',
            shortestLeaveInterval: ShortestLeaveIntervalEnum.fullDay,
            userLeaveTypeOverride: false,
            isActive: false,
            position: 0,
            color: '',
            ignoreRolloverFromPreviousYear: false,
            rolloverExpiryDate: '',
            seniorityEntitlementDaysForYear: 0,
            senioirtyEntitlementExists: false,
            entitlementByRoleDaysForYear: 0,
            entitlementByRoleExists: false,
            quota: 0,
            baseQuota: 0,
            toilDays: 0,
            toilDaysLapsed: 0,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            quotas: {},
          },
        ],
      },
    ],
    workWeekType: 'LOCATION',
    workWeekInfo: [],
    team: {
      id: '',
      name: '',
      approvers: [],
    },
    location: {
      id: '',
      name: '',
      workWeek: [1, 2, 3, 4, 5],
      workHours: [[1,8], [2,8], [3,8], [4,8], [5,8]],
      firstDayOfWeek: 1,
      rolloverNeverExpireDays: false,
      resetQuotas: 'FISCAL_YEAR',
      rolloverExpiryMonth: 0,
      rolloverExpiryDay: 0,
      rolloverExpiryAfterDays: 0,
      leavePolicies: [],
      timezone: '',
    },
    workWeek: [1, 2, 3, 4, 5],
    timestamp: '',
    upcomingLeaves: [],
    pendingLeaves: [],
    history: [],
    today: [],
    labels: [],
    locale: LocaleEnum.en,
    hourFormat: HourFormatEnum.twentyFour,
  })
  const [userInvitationStatus, setUserInvitationStatus] = useState<EmailInviteStatusEnum | undefined>(undefined)
  const [showLeaveRequestActionsButton, setShowLeaveRequestActionsButton] = useState(true)
  const [upcomingLeaves, setUpcomingLeaves] = useState<IUserUpcomingLeaves[]>([])
  const [historyLeaves, setHistoryLeaves] = useState<IUserHistory[]>([])
  const [pendingLeaves, setPendingLeaves] = useState<IUserPendingLeaves[]>([])
  const [currentLeaves, setCurrentLeaves] = useState<IUserToday[]>([])
  const [activeTab, setActiveTab] = useState<ProfileTabEnum>(() => {
    const searchParams = new URLSearchParams(location.search)
    const tab = searchParams.get('activeTab')
    if (isValidProfileTab(tab)) {
      return tab
    }
    return ProfileTabEnum.profile
  })

  // Read query param and set active tab when URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const tab = searchParams.get('activeTab')
    if (isValidProfileTab(tab)) {
      setActiveTab(tab)
    }
  }, [location.search])

  const [teams, setTeams] = useState<IGetTeamsShort[]>([])
  const [locations, setLocations] = useState<IGetLocationsShort[]>([])
  const [visibleLeaveForm, setVisibleLeaveForm] = useState(false)
  const [createLeaveLoader, setCreateLeaveLoader] = useState(false)
  const [userLabels, setUserLabels] = useState<ILabel[]>([])
  const [allLabels, setAllLabels] = useState<ILabel[]>([])
  const shouldEnableFeatures = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.labels)
  const [ editProfileModalVisible, showEditProfileModal ] = useState(false)
  const [connectedGoogleCalendar, setConnectedGoogleCalendar] = useState<IConnectedGoogleCalendar | null>(null)
  const [connectedOutlookCalendar, setConnectedOutlookCalendar] = useState<IConnectedOutlookCalendar | null>(null)
  const [isResendingLeaveRequest, setIsResendingLeaveRequest] = useState(false)
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState()
  const [probationPeriod, setProbationPeriod] = useState<IUserProbationPeriod>()

  const [ getUserByIdQuery ] = useManualQuery<IGetUserById, {
    id: string
    date: string
    pendingLeavesDate: string
  }>(getUserById)
  const [ getConnectedCalendarsQuery ] = useManualQuery<IGetConnectedCalendarsResponse, {
    userId: string
  }>(getConnectedCalendars)

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    history.push(FrontendUrls.energyLevel)
  }

  const approverCondition = (): boolean => {
    return amIApprover && authUser.id !== match.params.id && !authUser.approverTo.find(user => user.id === match.params.id)
  }
  const [getTeamsForApproverQuery] = useManualQuery<GetTeamsForApproverData, {
    id: string
    date: string
  }>(getTeamsForApprover)
  const [getAutomationsQuery] = useManualQuery<IGetAutomations>(getAutomations)
  const [getSuggestedSubstituteApproversQuery] = useManualQuery<IGetUsersForAdminAddLeaveData>(getSuggestedSubstituteApprovers)
  const [getInvitationsQuery] = useManualQuery<IGetInvitations>(getInvitations)
  const {
    setCheckIfSubstituteNeeded,
    suggestedSubstituteApprovers,
    shouldShowSubstituteApprovers,
    isSubstituteListLoading,
  } = useSubstituteApprover(
    {
      userId: user.id,
      startDate,
      endDate,
    },
    {
      getTeamsForApproverQuery,
      getAutomationsQuery,
      getSuggestedSubstituteApproversQuery,
    }
  )
  const [isMinutePickerEnabled, setIsMinutePickerEnabled] = useState(false)

  useEffect(() => {
    if (typeof enableMinutePickerLeave === 'undefined') {
      return
    }
    setIsMinutePickerEnabled(enableMinutePickerLeave)
  }, [enableMinutePickerLeave])

  useEffect(() => {
    if (approverCondition()) {
      return
    }
    fetchUser(match.params.id)
  }, [ match.params.id, actionNotifications ])

  useEffect(() => {
    if (leaveRequestActionEvent) {
      if (approverCondition()) {
        return
      }
      fetchUser(match.params.id)
      dispatch(setLeaveRequestActionEvent(null))
    }
  }, [leaveRequestActionEvent])

  if (approverCondition()) {
    return <NotFoundPage />
  }

  const handleOnEndDateSelected = (dateRange) => {
    const {startDate, endDate} = dateRange
    setStartDate(startDate)
    setEndDate(endDate)
    if(shouldEnableSubstituteApprovers) {
      setCheckIfSubstituteNeeded(true)
    }
  }

  const fetchUser = async (id: string) => {
    try {
      const response = await getUserByIdQuery({ variables: {
        id,
        date: dayjs().format('YYYY-MM-DD'),
        pendingLeavesDate: dayjs().subtract(2, 'years').startOf('year').format('YYYY-MM-DD'),
      }})
      if (!response.data || response.error) throw response.error

      const calendarStatusResponse = await getConnectedCalendarsQuery({ variables: { userId: id }})
      if (calendarStatusResponse.data?.getConnectedGooogleCalendar?.calendarId) {
        setConnectedGoogleCalendar(calendarStatusResponse.data.getConnectedGooogleCalendar)
      }
      if (calendarStatusResponse.data?.getConnectedOutlookCalendar?.calendarId) {
        setConnectedOutlookCalendar(calendarStatusResponse.data.getConnectedOutlookCalendar)
      }

      const user = response.data.getUser
      setUser(user)

      if (user.platform === 'email' && typeof user.isGhostUser === 'boolean' && user.isGhostUser && amIAdmin) {
        const invitations = await getInvitationsQuery()
        if (invitations.data) {
          setUserInvitationStatus(invitations.data.getInvitations.find(invitation => invitation.email === user.email || invitation.updatedEmail === user.email)?.status)
        }
      }

      const approversForUser = user.team.approvers
      const isMultilevelApproval = Boolean(approversForUser && approversForUser.find(approver => approver?.approverLevels?.includes(ApproversLevelEnum.SECOND)))
      if (isMultilevelApproval && amIAdmin) {
        const isAdminSelfApprover = approversForUser.find(approver => approver.id === user.id)
        if (!isAdminSelfApprover) {
          setShowLeaveRequestActionsButton(false)
        }
      }

      setUserLabels(user.labels || [])
      setUpcomingLeaves(user.upcomingLeaves.sort(
        (a: IUserUpcomingLeaves, b: IUserUpcomingLeaves) => a.startDate > b.startDate ? 1 : -1
      ))
      setCurrentLeaves(user.today)
      setHistoryLeaves(user.history.filter(leave => leave.status !== 'OPEN').sort(
        (a: IUserHistory, b:IUserHistory) => a.startDate > b.startDate ? -1 : 1
      ))

      const probationPeriod = response.data.getUser?.probationPeriod?.sort((a, b) => a.lengthDays > b.lengthDays ? -1 : 1)[0]
      if (probationPeriod) {
        const leaveTypes = user.location.leavePolicies.map(leavePolicy => leavePolicy.leaveType)

        const userProbationLeaveTypes = probationPeriod.leaveTypeIds
          .map(leaveTypeId => leaveTypes.find(leaveType => leaveType.id === leaveTypeId)?.name)
          .filter(Boolean)

        if (userProbationLeaveTypes.length > 0) {
          setProbationPeriod({
            ...probationPeriod,
            leaveTypeNames: userProbationLeaveTypes.length === 1 ? userProbationLeaveTypes[0] as string : userProbationLeaveTypes.join(', '),
          })
        }
      }

      setPendingLeaves(sortBy(user.pendingLeaves, ['startDate']))

      // TODO: remove this once we have the correct types - we should unify the types for the location and team lists
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      setLocations(response.data.getLocationListV2 || response.data.getLocationList || [])
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      setTeams(response.data.getTeamListV2 || response.data.getTeamList || [])

      setIsUserEndDateEnabled(Boolean(response.data.getCompany.userEndDate))

      setAllLabels(response.data.getLabelListV2 || [])
      setIsLoading(false)
      setIsResendingLeaveRequest(false)
    } catch (err) {
      logger.error('error fetching user by id', err)
    }
  }

  const onUpdateUser = async (data) => {
    try {
      if(!user) {
        throw new Error('Missing user')
      }
      const correlationIds: string[] = []

      if (data.changeTeam && data.teamId !== user?.team.id) {
        const response = await Api.post('/core/event', {
          eventType: 'TEAM_USER_MOVED',
          eventGroup: 'USER',
          teamId: data.teamId,
          userId: user?.id,
        })
        correlationIds.push(response.correlationId as string)
        const teamName = teams.find(t => t.id === data.teamId)?.name
        notification.open({
          key: response.correlationId,
          message: formatMessage({ id: 'user.team.moveInProgress' }, { name: teamName }),
          icon: (<LoadingOutlined />),
          duration: 0,
        })
      }

      if (data.changeLocation && data.locationId !== user.location.id) {
        const response = await Api.post('/core/event', {
          eventType: 'LOCATION_USER_MOVED',
          eventGroup: 'USER',
          locationId: data.locationId,
          userId: user.id,
        })
        correlationIds.push(response.correlationId as string)
        const locationName = locations.find(l => l.id === data.locationId)?.name
        notification.open({
          key: response.correlationId,
          message: formatMessage({ id: 'user.location.moveInProgress' }, { name: locationName }),
          icon: (<LoadingOutlined />),
          duration: 0,
        })
      }

      const event: IUserUpdatedEvent = {
        eventType: 'USER_UPDATED',
        eventGroup: 'USER',
        userId: user.id,
        name: data.name,
        email: data.email,
        platform: user.platform,
        startDate: data.startDate,
        endDate: data.endDate,
        employeeId: data.employeeId,
        isAdmin: data.isAdmin,
        changeEmail: data.changeEmail ?? false,
        isNameLocked: user.isNameLocked ? true : data.changeName,
        imageUrl: data.imageUrl ?? user.imageUrl ?? undefined,
        ...(data.changeStatus && data.status !== user.status && {status: data.status}),
      }

      const apiResponse = await Api.post('/core/event', event)
      correlationIds.push(apiResponse.correlationId as string)
      notification.open({
        key: apiResponse.correlationId,
        message: formatMessage({ id: 'user.updateInProgress' }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })

      setUser({
        ...user,
      })
      setActionNotifications([ ...actionNotifications, ...correlationIds ])
      setVisibleUserUpdateForm(false)
    } catch (error) {
      showErrorNotification(error)
    }
  }

  const onSaveUserProfileChanges = async (name: string, profilePicture?: RcFile) => {
    const params: Partial<IUserUpdatedEvent> = {
      eventType: 'USER_UPDATED',
      eventGroup: 'USER',
      userId: user.id,
      name,
    }

    if (profilePicture) {
      const url = await uploadAvatar(profilePicture)
      params.imageUrl = url
    }

    const response = await Api.post('/core/event', params)
    if (response.correlationId) {
      setActionNotifications([...actionNotifications, response.correlationId])
    }
    showEditProfileModal(false)
    setVisibleUserUpdateForm(false)
  }

  const handleTabChange = (event: { key: string }) => {
    if (event.key !== 'link' && isValidProfileTab(event.key)) {
      setActiveTab(event.key)
      // Update URL query param when tab changes
      history.replace(`${location.pathname}?activeTab=${event.key}`)
    }
  }

  const deleteLeave = async (leaveRequestId: string) => {
    try {
      if (!user) {
        throw new Error('Missing user')
      }
      const isToil = isToilLeave(leaveRequestId)

      const body = {
        eventType: isToil ? 'TOIL_REQUEST_DELETED' : 'LEAVE_REQUEST_DELETED',
        eventGroup: isToil ? 'USER_TOIL_REQUEST' : 'USER_LEAVE_REQUEST',
        userId: user.id,
      }
      isToil ? body['toilRequestId'] = leaveRequestId : body['leaveRequestId'] = leaveRequestId

      const response = await Api.post('/core/event', body)
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: isToil ? 'components.toil.deleteInProgress' : 'user.deleteLeaveInProgress' }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })
      setActionNotifications([ ...(actionNotifications ?? []), response.correlationId ])
    } catch (error) {
      showErrorNotification(error)
    }
  }

  const cancelLeave = async (leaveRequestId: string) => {
    try {
      if (!user) {
        throw new Error('Missing user')
      }

      const isToil = isToilLeave(leaveRequestId)
      const body = {
        eventType: isToil ? 'TOIL_REQUEST_CANCELLED' : 'LEAVE_REQUEST_CANCELLED',
        eventGroup: isToil ? 'USER_TOIL_REQUEST' : 'USER_LEAVE_REQUEST',
        userId: user.id,
      }
      isToil ? body['toilRequestId'] = leaveRequestId : body['leaveRequestId'] = leaveRequestId

      const response = await Api.post('/core/event', body)
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: isToil ? 'components.toil.cancelInProgress' : 'user.cancelLeaveRequestInProgress' }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })
      setActionNotifications([ ...(actionNotifications ?? []), response.correlationId ])
    } catch (error) {
      showErrorNotification(error)
    }
  }

  const handleUpdateWorkweek = async (userWorkHours: WorkingHours | IWorkingSchedule) => {
    try {
      if (!user) {
        throw new Error('Missing user')
      }

      if (getAverageWorkingHours(userWorkHours) === 0) {
        showErrorNotification(formatMessage({ id: 'app.zeroHours' }))
        return
      }

      const workWeekPart = userHasRotatingSchedule(userWorkHours) ? {
        schedule: {
          workHours: (userWorkHours as IWorkingSchedule).workHours,
          startDate: dayjs((userWorkHours as IWorkingSchedule).startDate).format('YYYY-MM-DD'),
        },
      } : {
        days: (userWorkHours as WorkingHours).map(([day]) => day),
        workHours: userWorkHours,
      }

      const response = await Api.post('/core/event', {
        eventType: 'USER_WORK_WEEK_UPDATED',
        eventGroup: 'USER',
        userId: user.id,
        ...workWeekPart,
      })
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'user.workWeek.updateInProgress' }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })
      setActionNotifications([ ...actionNotifications, response.correlationId ])
    } catch (error) {
      showErrorNotification(error)
    }
  }

  const renderTabBar = () => {
    return <></>
  }

  const onUpdateLeaveRequest = async (leaveRequestId: string, leaveStatus, statusReason?: string) => {
    try {
      if (!user) {
        throw new Error('Missing user')
      }

      let eventGroup: string, eventType: string, messageId: string
      const isToil = isToilLeave(leaveRequestId)
      if (isToil) {
        eventGroup = 'USER_TOIL_REQUEST'
        eventType = leaveStatus ? 'TOIL_REQUEST_APPROVED' : 'TOIL_REQUEST_DENIED'
        messageId = leaveStatus ? 'components.toil.approveInProgress' : 'components.toil.denyInProgress'
      } else {
        eventType = 'LEAVE_REQUEST_STATUS_CHANGED'
        eventGroup = 'USER_LEAVE_REQUEST'
        messageId = leaveStatus ? 'user.approveInProgress' : 'user.denyInProgress'
      }

      const body = {
        eventType,
        eventGroup,
        userId: user.id,
        statusReason,
        ...((eventType === 'LEAVE_REQUEST_STATUS_CHANGED') ? { leaveStatus: leaveStatus ? 'APPROVED' : 'DENIED'} : {}),
      }
      isToil ? body['toilRequestId'] = leaveRequestId : body['leaveRequestId'] = leaveRequestId

      const response = await Api.post('/core/event', body)
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: messageId }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })
      setActionNotifications([ ...(actionNotifications ?? []), response.correlationId ])
    } catch (error) {
      logger.warning(error)
      showErrorNotification(error)
    }
  }

  const showErrorNotification = (error) => {
    const errorDescription = error.response?.data?.message ? error.response?.data.message : error.message ? error.message : JSON.stringify(error)
    notification.error({
      message: formatMessage({ id: 'error.generic' }),
      description: errorDescription,
      duration: 0,
    })
  }

  const handleSubmitLeave = async (data: ILeaveFormSaveData) => {
    setCreateLeaveLoader(true)
    let response
    try {
      if (!user) {
        throw new Error('Missing user')
      }

      response = await Api.post('/core/leave-request-validate', {
        eventType: 'LEAVE_REQUEST_ADDED',
        eventGroup: 'USER_LEAVE_REQUEST',
        ...data,
        userId: user.id,
      })
      response = await Api.post('/core/event', {
        eventType: 'LEAVE_REQUEST_ADDED',
        eventGroup: 'USER_LEAVE_REQUEST',
        ...data,
        userId: user.id,
      })
      setCreateLeaveLoader(false)
      setVisibleLeaveForm(false)

      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'addLeave.inProgress' }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })
      setActionNotifications([ ...actionNotifications, response.correlationId ])
    } catch (error) {
      setCreateLeaveLoader(false)
      setVisibleLeaveForm(false)
      if (error.response?.data?.error || error.response.data.message) {
        notification.error({
          message: formatMessage({ id: 'error.leaveSubmitError' }),
          description: formatMessage({ id: error.response.data.error }),
          duration: 0,
        })
      } else {
        const description = response?.correlationId ? formatMessage({ id: 'app.correlationIdError' }, { correlationId: response.correlationId }) : JSON.stringify(error)

        notification.error({
          message: formatMessage({ id: 'error.leaveSubmitError' }),
          description,
          duration: 0,
        })
      }
    }
  }

  const onQuotaUpdate = async (updates: IQuotaUpdates | IRolloverQuotaUpdates) => {
    const response = await Api.post('/core/event', updates)

    if (updates.eventType === 'USER_LEAVE_TYPES_UPDATED') {
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'users.updateQuota.InProgress' }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })
      setActionNotifications([ ...actionNotifications, response.correlationId ])
    } else if (updates.eventType === 'USER_LEAVE_TYPES_ROLLOVER_UPDATED') {
      fetchUser(match.params.id)
      notification.success({
        message: formatMessage({ id: 'users.updateRolloverQuota.successTitle' }),
        duration: 7,
      })
    }
  }

  const handleSelectLabel = async (value) => {
    if (!value.id) {
      const response = await Api.post('/core/event', {
        eventType: 'LABEL_CREATED',
        eventGroup: 'LABEL',
        name: value.name,
        color: value.color,
      })
      value.id = response.labelId
      value.userCount = 1
      setAllLabels(prevState => {
        return [...prevState, value]
      })
    } else {
      setAllLabels(prevState => {
        return prevState.map(state => {
          if (state.id === value.id && state.userCount) {
            state.userCount++
          }
          return state
        })
      })
    }

    setUserLabels(prevState => {
      return [...prevState, {id: value.id, name: value.name, color: value.color}]
    })

    await Api.post('/core/event', {
      eventType: 'LABEL_ASSIGNED_TO_USER',
      eventGroup: 'LABEL',
      labelId: value.id,
      name: value.name,
      userId: user.id,
    })

    setTimeout(() => {
      fetchUser(match.params.id)
    }, 2000)
  }

  const handleDeselectLabel = async (value) => {
    setUserLabels(prevState => {
      return prevState.filter((state => state.id !== value.id))
    })

    if (value?.userCount === 1) {
      setAllLabels(prevState => {
        return prevState.filter((state => state.id !== value.id))
      })
    } else {
      setAllLabels(prevState => {
        return prevState.map(state => {
          if (state.id === value.id && state.userCount) {
            state.userCount--
          }
          return state
        })
      })
    }

    await Api.post('/core/event', {
      eventType: 'LABEL_REMOVED_FROM_USER',
      eventGroup: 'LABEL',
      labelId: value.id,
      userId: user.id,
    })

    setTimeout(() => {
      fetchUser(match.params.id)
    }, 2000)
  }

  const updateLabel = async (value, labelId): Promise<void> => {
    setAllLabels(prevState => {
      return prevState.map(label => {
        if(label.id === labelId) {
          label.name = value.name
          label.color = value.color
        }

        return label
      })
    })
    setUserLabels(prevState => {
      return prevState.map(label => {
        if(label.id === labelId) {
          label.name = value.name
          label.color = value.color
        }

        return label
      })
    })

    await Api.post('/core/event', {
      eventType: 'LABEL_UPDATED',
      eventGroup: 'LABEL',
      labelId,
      name: value.name,
      color: value.color,
    })
  }

  const deleteLabel = async(labelId: string): Promise<void> => {
    await Api.post('/core/event', {
      eventType: 'LABEL_DELETED',
      eventGroup: 'LABEL',
      labelId,
    })

    setAllLabels(prevState => {
      return prevState.filter(label => label.id !== labelId)
    })
    setUserLabels(prevState => {
      return prevState.filter((state => state.id !== labelId))
    })
  }

  const onResendLeaveRequest = async (data: IResendLeaveRequest) => {
    setIsResendingLeaveRequest(true)
    let response
    try {
      const isToilRequest = isToilLeave(data.resentLeaveRequestId as string)
      const type = isToilRequest ? 'TOIL' : 'LEAVE'
      let params = data
      if (isToilRequest) {
        data['resentRequestId'] = data['resentLeaveRequestId']
        params = omit(data, 'resentLeaveRequestId')
      }
      response = await Api.post(`/core/${lowerCase(type)}-request-validate`, {
        eventType: `${type}_REQUEST_CREATED`,
        eventGroup: `USER_${type}_REQUEST`,
        ...params,
        userId: user.id,
      })

      response = await Api.post('/core/event', {
        eventType: `${type}_REQUEST_CREATED`,
        eventGroup: `USER_${type}_REQUEST`,
        ...params,
        userId: user.id,
      })
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: isToilRequest ? 'components.toil.requestToilProgress' : 'requestLeave.inProgress' }),
        icon: (<LoadingOutlined />),
        duration: 0,
      })
      setActionNotifications([ ...actionNotifications, response.correlationId ])
    } catch (error) {
      setIsResendingLeaveRequest(false)
      logger.warning('error', error)
      if (error?.response?.data?.code === 'BLACKOUT_PERIOD') {
        const data = error?.response?.data?.data
        notification.error({
          message: formatMessage({
            id: 'automations.blackoutPeriodFromTo',
          }, {
            fromDate: dayjs(data.startDate).format('MMMM Do YYYY.'),
            toDate: dayjs(data.endDate).format('MMMM Do YYYY.'),
            endDate: (...chunks: React.ReactNode[]) => data.startDate !== data.endDate ? <>{chunks}</> : '',
          }),
          description: error.response.data.message,
          duration: 0,
        })
      } else if (error?.response?.data?.code === 'PROBATION_PERIOD') {
        const data = error?.response?.data?.data
        notification.error({
          message: formatMessage({ id: 'automations.probationPeriodMessages' }, { date: dayjs(data.date as string).format('MMMM Do YYYY.')}),
          description: error.response.data.message,
          duration: 0,
        })
      } else if (error?.response?.data?.code === 'LEAVE_DURATION_LIMIT') {
        notification.error({
          message: formatMessage(
            { id: 'automations.LEAVE_DURATION_LIMIT' }
          ),
          description: error.response.data.message,
          duration: 0,
        })
      } else if (error?.response?.data?.code === 'LEAVE_REQUEST_NOTICE_PERIOD') {
        notification.error({
          message: formatMessage(
            { id: 'automations.LEAVE_REQUEST_NOTICE_PERIOD' }
          ),
          description: error.response.data.message,
          duration: 0,
        })
      } else if (error.response?.data?.error || error.response.data.message) {
        notification.error({
          message: formatMessage({ id: 'error.leaveSubmitError' }),
          description: formatMessage({ id: error.response.data.message || error.response?.data?.error }),
          duration: 0,
        })
      } else {
        const description = response?.correlationId ? formatMessage({ id: 'app.correlationIdError' }, { correlationId: response.correlationId }) : JSON.stringify(error)

        notification.error({
          message: formatMessage({ id: 'error.leaveSubmitError' }),
          description,
          duration: 0,
        })
      }
    }
  }

  const sendInvite = async (email: string) => {
    try {
      const response = await Api.post('/core/event', {
        eventType: 'USERS_INVITED',
        eventGroup: 'USER',
        emails: [email],
        userId: authUser.id,
        locale: LocaleEnum.en,
      })
      setActionNotifications([...actionNotifications, response.correlationId])
    } catch (error) {
      logger.error(error)
    }
  }

  const shouldEnableEditForEmailPlatformUser = authUser.id === user.id && authUser.platform === 'email'

  const userProfileTabs: TabsProps['items'] = [
    {
      key: ProfileTabEnum.profile,
      label: <IntlMessages id="user.profile" />,
      children: <UserProfileTab
        amIAdmin={amIAdmin}
        user={{
          id: user.id,
          isAdmin: user.isAdmin,
          email: user.email,
          name: user.name,
          team: user.team,
          platform: user.platform as Platform,
          location: user.location,
          startDate: user.startDate,
          endDate: user.endDate,
          employeeId: user.employeeId,
          role: user.role,
          status: user.status,
          workWeekType: user.workWeekType,
          workWeekInfo: user.workWeekInfo,
          workHours: getUserWorkingHoursDependingOnType(user.workHours) || [],
          isNameLocked: user.isNameLocked,
          approvers: user.team.approvers ? user.team.approvers.map(approver => approver.name) : [],
          imageUrl: user?.imageUrl,
          locale: locale.locale,
          isGhostUser: user.isGhostUser,
          userInvitationStatus,
        }}
        workingHours={getUserWorkingHoursDependingOnType(user.workHours) || []}
        firstDayOfWeek={user.location.firstDayOfWeek}
        quotas={user.leaveDays}
        teams={teams}
        locations={locations}
        isUserEndDateEnabled={isUserEndDateEnabled}
        hourlyLeaveAccounting={Boolean(authCompany?.hourlyLeaveAccounting)}
        showUpdateUserForm={visibleUserUpdateForm}
        setShowUpdateUsersForm={setVisibleUserUpdateForm}
        onQuotaUpdate={onQuotaUpdate}
        updateUserProfile={onUpdateUser}
        updateWorkingDays={handleUpdateWorkweek}
        setActiveTab={handleTabChange}
        onEdit={shouldEnableEditForEmailPlatformUser
          ? () => showEditProfileModal(true)
          : () => {return}
        }
        sendInvite={sendInvite}
      />,
    },
    {
      key: ProfileTabEnum.leaves,
      label: <IntlMessages id="user.leaves" />,
      children: <UserLeavesTab
        amIAdmin={amIAdmin}
        amIApprover={amIApprover}
        pendingRequests={pendingLeaves}
        currentLeaves={currentLeaves}
        scheduledLeaves={upcomingLeaves}
        leaveHistory={historyLeaves}
        user={{
          id: user.id,
          name: user.name,
          locale: user.locale,
          hourFormat: authUser.hourFormat || HourFormatEnum.twentyFour,
          locationTimezone: user.location.timezone,
        }}
        onCancelLeave={cancelLeave}
        onDeleteLeave={deleteLeave}
        onLeaveRequestUpdate={onUpdateLeaveRequest}
        onResendLeaveRequest={onResendLeaveRequest}
        isResendingLeaveRequest={isResendingLeaveRequest}
        showLeaveRequestActionsButton={showLeaveRequestActionsButton}
      />,
    },
    {
      key: ProfileTabEnum.logs,
      label: <IntlMessages id="app.logs" />,
      children: <UserLogsTab
        userId={match.params.id}
        amIAdmin={amIAdmin}
        reload={activeTab}
        locationTimezone={user.location.timezone}
        hourlyLeaveAccounting={Boolean(authCompany?.hourlyLeaveAccounting)}
        hourFormat={authUser.hourFormat || HourFormatEnum.twentyFour}
        workingHours={getUserWorkingHoursDependingOnType(user.workHours) || []}
      />,
    },
  ]

  return (
    <>
      {isLoading ?
        <CircularProgress /> :
        <>
          <div className="profile-banner">
            {probationPeriod &&
              <Alert
                message={<IntlMessages id="user.probationPeriodDescription"
                  values={{
                    leaveTypeNames: () => <strong>{probationPeriod.leaveTypeNames}</strong>,
                    probationPeriodName: () => <strong>{probationPeriod.name}</strong>,
                    lengthDays: () => <strong>{probationPeriod.lengthDays}</strong>,
                    date: () => <strong>{dayjs(user.startDate).add(probationPeriod.lengthDays, 'days').format('MMMM Do YYYY')}</strong>,
                  }}
                />}
                type="warning"
                style={{ top: -20 }}
              />
            }
            <div className="profile-container">
              <div className="profile-banner-top center-content">
                <Row>
                  <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 8 }} lg={{ span: 8 }} xl={{ span: 8 }} xxl={{ span: 12 }}>
                    <div className="profile-banner-top-left">
                      {(() => {
                        const isOwnProfile = authUser.id === user.id
                        const canClickAvatar = isOwnProfile && isEnergyScoreEnabled
                        const avatarElement = (
                          <div
                            className="profile-banner-avatar"
                            onClick={canClickAvatar ? handleAvatarClick : undefined}
                            style={{
                              alignSelf: 'flex-start',
                              cursor: canClickAvatar ? 'pointer' : 'default',
                              height: '90px',
                              width: '90px',
                            }}
                          >
                            {shouldEnableEditForEmailPlatformUser &&
                              <div
                                className="profile-banner-avatar-overlay"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  return amIAdmin ? setVisibleUserUpdateForm(true) : showEditProfileModal(true)
                                }}>
                                <IntlMessages id="app.edit" />
                              </div>
                            }
                            <UserAvatar id={user.id} avatar={user.imageUrl} name={user.name} avatarSize={90} shape="circle"
                              showEnergy={shouldShowEnergyScore} energyScore={energyScore} />
                          </div>
                        )

                        return canClickAvatar ? (
                          <Tooltip title={
                            <div style={{ textAlign: 'center' }}>
                              <IntlMessages id="app.userProfile.energyLevelTooltip" defaultMessage="Click to view Energy Level Dashboard" /></div>
                          }
                          placement="top">
                            {avatarElement}
                          </Tooltip>
                        ) : avatarElement
                      })()}
                      <div className="profile-banner-avatar-info" style={{ flex: 1 }}>
                        <h2>{user.name}&nbsp;{user.adpIntegrated && <ADPFlag />}</h2>
                        {shouldEnableFeatures  ?
                          <UserLabels
                            isEditable={Boolean(amIAdmin)}
                            allLabels={allLabels}
                            userLabels={userLabels}
                            handleSelectLabel={handleSelectLabel}
                            handleDeselectLabel={handleDeselectLabel}
                            updateLabel={updateLabel}
                            deleteLabel={deleteLabel}
                          /> :
                          <p>{user.team.name}</p>
                        }
                      </div>
                    </div>
                  </Col>
                  <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 16 }} lg={{ span: 16 }} xl={{ span: 16 }} xxl={{ span: 12 }}>
                    {currentLeaves.length > 0 &&
                      <UserTodayOff todayLeaves={currentLeaves} />
                    }
                  </Col>
                </Row>
              </div>
              <div className="profile-banner-bottom center-content">
                <Menu
                  onClick={handleTabChange}
                  selectedKeys={[activeTab]}
                  mode="horizontal"
                  items={[
                    {
                      key: ProfileTabEnum.profile,
                      label: <IntlMessages id="user.profile" />,
                    },
                    {
                      key: ProfileTabEnum.leaves,
                      label: <>
                        <IntlMessages id="user.leaves" />
                        <Badge style={{marginLeft: 4}} count={pendingLeaves.length} />
                      </>,
                    },
                    {
                      key: ProfileTabEnum.logs,
                      label: <IntlMessages id="app.logs" />,
                    },
                    {
                      key: 'link',
                      label: <IntlMessages id="app.addLeave" />,
                      onClick: () => { setVisibleLeaveForm(true) },
                      className: 'btn',
                    },
                  ]}
                />
                <Breadcrumb
                  items={[
                    {
                      title: <Link to={FrontendUrls.dashboard}><IntlMessages id="sidebar.dashboard" /></Link>,
                    },
                    {
                      title: <Link to='/app/users'><IntlMessages id="app.users" /></Link>,
                    },
                    {
                      title: user.name,
                    },
                  ]}
                />
              </div>
            </div>
          </div>
          <div className="user-page center-content">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => handleTabChange({ key })}
              renderTabBar={renderTabBar}
              items={userProfileTabs}
            />
          </div>
          {visibleLeaveForm &&
            <Modal
              title={<IntlMessages id='app.addLeave' />}
              open={visibleLeaveForm}
              onCancel={() => { setVisibleLeaveForm(false) }}
              footer={false}
            >
              <LeaveForm
                authUserId={authUser.id}
                authUserRole={authUser.role}
                hourFormat={authUser.hourFormat}
                loading={createLeaveLoader}
                listOfUsers={[user]}
                onEndDateSelected={handleOnEndDateSelected}
                onSave={(data: ILeaveFormSaveData) => {
                  (async () => {
                    await handleSubmitLeave(data)
                  })()
                }}
                onCancel={() => { setVisibleLeaveForm(false) }}
                formType={'add-to-user'}
                isMinutePickerEnabled={isMinutePickerEnabled}
                modalForm={true}
                connectedGoogleCalendar={connectedGoogleCalendar}
                connectedOutlookCalendar={connectedOutlookCalendar}
                shouldShowSubstituteApprovers={shouldShowSubstituteApprovers}
                substituteApprovers={suggestedSubstituteApprovers}
                isSubstituteListLoading={isSubstituteListLoading}
              />
            </Modal>
          }
          {shouldEnableEditForEmailPlatformUser && editProfileModalVisible && <UserMyProfileModal
            name={user.name}
            profilePhoto={user.imageUrl}
            visible={editProfileModalVisible}
            onCancel={() => showEditProfileModal(false)}
            onSave={onSaveUserProfileChanges}
          />}
        </>
      }
    </>
  )
}

export default UserPage
